/* ============================================================================
   Diyona Optimistic Cart  (window.DiyonaOptimisticCart)
   swap v3 (2026-09-18): preserve the chosen diamond when confirmation fails;
   show recovery actions and validate a complete, settled cart on every checkout.
   Only remove a placeholder after its real diamond is verified in the cart.
   Rebuilt 2026-06-19 from project_diamond_atc_optimization.md spec, targeting
   STEP 3 of the ring builder (paired diamond + setting).

   Flow: complete-ring ATC adds a $0 SENTINEL diamond line (variant 51975403077948)
   with all diamond data in line-item properties + the REAL setting line, then
   redirects to /cart immediately. This script, on /cart, renders nothing itself
   (cart-item.liquid renders the placeholder from properties) but:
     1) gates checkout while any pending diamond line exists,
     2) keeps placeholder estimates separate from the native payable total,
     3) in the background calls create-product?type=Ring, ADDS the real diamond
        variant (same properties), then REMOVES the sentinel line (add-then-remove
        so the cart never lacks the item), then refreshes the cart UI.
   ========================================================================== */
(function () {
  /* tt-aware helpers: inert on the real site (window.__tt2pjaxNavigate is
     undefined there); inside the TikTok single-document clone they keep cart
     refreshes in-document instead of hard-reloading the webview. */
  function ttReload() {
    if (window.__tt2pjaxNavigate) { window.__tt2pjaxNavigate(window.location.pathname + window.location.search, { pop: true }); }
    else { window.location.reload(); }
  }
  function isCartPageLive() { return /\/cart(\b|\/|$)/.test(window.location.pathname); }
  var SENTINEL_VARIANT = 51975403077948;
  var API_BASE = 'https://diamond-ring-builder-flax.vercel.app/api';
  var PENDING_PROP = '_pending_diamond_id';
  var OP_PROP = '_diy_operation_v1';
  var operationMemory = {}, operationLocks = {};
  var OP_PREFIX = 'diyCartOperation:v1:';
  var CANCEL_PREFIX = 'diyCartCancelled:v1:';

  function cartRoute(path) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    return root.replace(/\/?$/, '/') + path.replace(/^\//, '');
  }
  function cartError(code, message, operationId) {
    var error = new Error(message); error.code = code;
    if (operationId) error.operationId = operationId;
    return error;
  }
  function readOperation(key) {
    try { var stored = window.localStorage && window.localStorage.getItem(key); if (stored) return JSON.parse(stored); } catch (e) {}
    return operationMemory[key] || null;
  }
  function writeOperation(key, value) {
    operationMemory[key] = value;
    try { if (window.localStorage) window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }
  function operationOf(line) { var p = line && line.properties || {}; return p[OP_PROP] || p[PENDING_PROP] || null; }
  function cancelPurchase(operationId) {
    if (operationId) writeOperation(CANCEL_PREFIX + operationId, { cancelled: true, at: Date.now() });
  }
  function isCancelled(operationId) { return !!(operationId && readOperation(CANCEL_PREFIX + operationId)); }
  // Web Locks coordinate current same-origin tabs when available. This is not a
  // server/cart transaction and cannot control legacy pages or direct Ajax writers.
  function withOperationLock(key, work) {
    if (window.navigator && window.navigator.locks && window.navigator.locks.request) {
      return window.navigator.locks.request('diyona-cart:' + key, work);
    }
    var previous = operationLocks[key] || Promise.resolve();
    var next = previous.catch(function () {}).then(work);
    operationLocks[key] = next;
    return next.then(function (value) { if (operationLocks[key] === next) delete operationLocks[key]; return value; }, function (error) { if (operationLocks[key] === next) delete operationLocks[key]; throw error; });
  }
  function notifyMutation(phase, operationId, source) {
    document.dispatchEvent(new CustomEvent('diyona:cart-mutation', { detail: { phase: phase, operationId: operationId || null, source: source || 'optimistic-cart' } }));
  }
  var ownedPreflights=new WeakSet(),ownedVerifiedReads=new WeakMap(),ownedVerificationActive=0,ownedReplacementRequired={};
  function ownedSelection(d) {
  return !!d && (d.inventory_source === 'owned' || d.is_diyona_select === true);
}
  function ownedNativeIdentity(line, expected) {
  expected = expected || {};
  if (expected._inventory_source !== 'owned' && expected._diyona_select !== 'true') return true;
  if (!line || line.sku !== expected._diamond_sku || Number(line.quantity) !== 1 || Number(line.variant_id) === SENTINEL_VARIANT || (line.properties || {})[PENDING_PROP]) return false;
  // Older confirmed owned cart lines predate these native identity properties.
  // Every new owned add carries both; never accept a partially populated pair.
  if (expected._owned_product_id == null && expected._owned_variant_id == null) return true;
  return /^\d+$/.test(String(expected._owned_product_id)) && /^\d+$/.test(String(expected._owned_variant_id)) &&
    Number.isSafeInteger(Number(expected._owned_product_id)) && Number(expected._owned_product_id) > 0 &&
    Number.isSafeInteger(Number(expected._owned_variant_id)) && Number(expected._owned_variant_id) > 0 &&
    String(line.product_id) === String(expected._owned_product_id) && String(line.variant_id) === String(expected._owned_variant_id);
}
  function ownedSelectionMatches(line, d) {
  return !!line && DiyonaInventory.owned(d) && Number(line.variant_id) === DiyonaInventory.variant(d) &&
    String(line.product_id) === String(d.shopify_product_id) && line.sku === d.sku && Number(line.quantity) === 1 &&
    !(line.properties || {})[PENDING_PROP] && ownedNativeIdentity(line, line.properties);
}
  function captureOwnedCart() {
  var sequence=externalMutationSequence,context=cartContext();
  return getCart().then(function(cart) {
    var preflight={cart:cart,sequence:sequence,context:context,readAt:Date.now()};
    ownedPreflights.add(preflight);
    return preflight;
  });
}
  function ownedCartCurrent(preflight) {
  return ownedPreflights.has(preflight) && preflight.context === cartContext() &&
    preflight.sequence === externalMutationSequence && !cartWriteActive() && Date.now()-preflight.readAt <= 1500;
}
  function settleOwnedCart(preflight) {
  if (preflight.context !== cartContext()) return Promise.reject(cartError('DIY_CART_CHANGED','Your shopping context changed. Please retry.'));
  if (ownedCartCurrent(preflight)) return Promise.resolve(preflight);
  ownedPreflights.delete(preflight);
  return captureOwnedCart().then(function(current) {
    if (!ownedCartCurrent(current)) throw cartError('DIY_CART_CHANGED','Your bag is being updated. Please finish that change and retry.');
    return current;
  });
}
  function consumeOwnedCart(preflight) {
  if (!ownedCartCurrent(preflight)) throw cartError('DIY_CART_CHANGED','Your bag changed. Please retry.');
  ownedPreflights.delete(preflight);
  return preflight.cart;
}
  function readOwnedVerifiedCart(retried) {
  var context=cartContext(),sequence=validationSequence;
  return getCart().then(function(cart) {
    if (context !== cartContext()) throw cartError('DIY_CART_CHANGED','Your shopping context changed. Please review your bag.');
    if (sequence !== validationSequence || cartWriteActive()) {
      if (retried) throw cartError('DIY_CART_CHANGED','Your bag is being updated. Please review it before retrying.');
      return readOwnedVerifiedCart(true);
    }
    ownedVerifiedReads.set(cart,{context:context,sequence:sequence});
    return cart;
  });
}
  function addOwnedVerified(items, operationId, preflight) {
  var context=cartContext();
  ownedVerificationActive++;
  return addItemsVerified(items,{operationId:operationId,ownedPreflight:preflight}).then(function(result) {
    ownedVerificationActive--;
    var read=ownedVerifiedReads.get(result.cart);
    if (read && read.context === cartContext() && read.sequence === validationSequence && !cartWriteActive()) applyCartValidation(result.cart);
    else {
      setCheckoutBlocked(true);
      if (context === cartContext() && !cartWriteActive()) refreshCartValidation();
    }
    return result;
  },function(error) {
    ownedVerificationActive--;
    setCheckoutBlocked(true);
    throw error;
  });
}
  function ownedRingPurchase(opts, allowReplacement) {
  return withOperationLock('purchase:'+opts.diamond.sku,function() {
    var operationId=purchaseAttempt(opts).value.id;
    // Eligibility (and an optional engraving lookup) overlap the cart read.
    // A mutation or a slow lookup discards that read before any native add.
    return Promise.all([ringPayload(opts,operationId),captureOwnedCart()]).then(function(values) {
      var items=values[0];
      return settleOwnedCart(values[1]).then(function(preflight) {
        var cart=preflight.cart,existing=findDiamondInCart(cart,opts.diamond.sku);
        if (existing && existing.ambiguous) throw cartError('DIY_CART_PARTIAL','This ring needs review in your bag before it can be changed.');
        if (existing) {
          var bundle=cart.items.filter(function(item){return existing.keys.indexOf(item.key)!==-1;});
          var diamond=bundle.filter(function(item){return item.key===existing.diamondKey;})[0];
          var state=validateCheckoutCart({items:bundle});
          if (!ownedSelectionMatches(diamond,opts.diamond) || state.pending.length || state.incomplete.length) throw cartError('DIY_CART_PARTIAL','This selection needs review in your bag before it can be changed.');
        }
        if (sameRingSelection(cart,existing,opts)) return {added:true,alreadyInCart:true,cart:cart};
        if (existing) {
          if (allowReplacement) return ownedReplacementRequired;
          throw cartError('DIY_CART_PARTIAL','This diamond is already in your bag. Review or update that selection before adding it again.');
        }
        if (opts.diamond.sku) delete removeIntents[opts.diamond.sku];
        return addOwnedVerified(items,operationId,preflight);
      });
    });
  }).then(function(result) {
    // Existing replacements retain the complete removal/rollback journal and
    // fresh reads. Release the purchase lock before entering that same lock.
    return result === ownedReplacementRequired ? replaceRingLegacy(opts) : result;
  });
}
  function ownedLoosePurchase(d, shipBy) {
  return withOperationLock('purchase:'+d.sku,function() {
    var attempt=purchaseAttempt({diamond:d,operationId:d.operationId});
    return Promise.all([DiyonaInventory.prepare(d,'Loose'),captureOwnedCart()]).then(function(values) {
      var current=values[0];
      return settleOwnedCart(values[1]).then(function(preflight) {
        var cart=preflight.cart,existing=findDiamondInCart(cart,d.sku);
        if (existing) {
          var line=cart.items.filter(function(item){return item.key===existing.diamondKey;})[0];
          var state=validateCheckoutCart({items:cart.items.filter(function(item){return existing.keys.indexOf(item.key)!==-1;})});
          if (existing.ambiguous || !ownedSelectionMatches(line,current) || state.pending.length || state.incomplete.length) throw cartError('DIY_CART_PARTIAL','This diamond needs review in your bag before it can be added again.');
          return {alreadyInCart:true,cart:cart};
        }
        if (d.sku) delete removeIntents[d.sku];
        return addOwnedVerified([buildLooseLineItem(current,shipBy)],attempt.value.id,preflight);
      });
    });
  });
}
  function optimisticAddRing(opts) { return ownedSelection(opts.diamond) ? ownedRingPurchase(opts,false) : optimisticAddRingLegacy(opts); }
  function replaceRing(opts) { return ownedSelection(opts.diamond) ? ownedRingPurchase(opts,true) : replaceRingLegacy(opts); }
  function optimisticAddLoose(d, shipBy) { return ownedSelection(d) ? ownedLoosePurchase(d,shipBy) : optimisticAddLooseLegacy(d,shipBy); }

  function purchaseSignature(opts) {
    return JSON.stringify([opts.diamond && opts.diamond.sku, opts.setting && opts.setting.variantId, opts.sizeLabel === undefined ? opts.size || null : opts.sizeLabel, opts.engrave || '']);
  }
  function purchaseAttempt(opts) {
    var signature = purchaseSignature(opts), key = OP_PREFIX + encodeURIComponent(signature);
    var attempt = readOperation(key);
    if (!attempt || (opts.operationId && attempt.id !== opts.operationId) || isCancelled(attempt.id)) {
      attempt = { id: opts.operationId || uuid(), signature: signature, status: 'new' };
      writeOperation(key, attempt);
    }
    return { key: key, value: attempt };
  }
  function stampItems(items, operationId) {
    return items.map(function (item) {
      return { id: item.id, quantity: item.quantity, properties: Object.assign({}, item.properties || {}, (function () { var p = {}; p[OP_PROP] = operationId; return p; })()) };
    });
  }
  function matchingAttempt(cart, items, operationId) {
    var owned = (cart.items || []).filter(function (line) { return line.properties && line.properties[OP_PROP] === operationId; });
    var complete = owned.length === items.length && items.every(function (item) {
      var expected = item.properties || {};
      var found = owned.filter(function (line) {
        if (Number(item.id) === SENTINEL_VARIANT && Number(line.variant_id) !== SENTINEL_VARIANT) {
          return verifiedDiamond(line, expected._diamond_sku) && line.quantity === item.quantity;
        }
        return ownedNativeIdentity(line, expected) && Number(line.variant_id) === Number(item.id) && line.quantity === item.quantity && Object.keys(expected).every(function (key) {
          if (key === PENDING_PROP || key === '_idempotency_key') return true;
          // Advisory display snapshots can change between reads of the same purchase.
          if (key === '_display_price' || key === '_display_currency' || key === '_display_country' || key === '_display_price_source' || key === '_display_price_version') return true;
          return String((line.properties || {})[key]) === String(expected[key]);
        });
      });
      return found.length === 1;
    });
    return { complete: complete, owned: owned };
  }
  function addItemsVerified(items, options) {
    options = options || {};
    var operationId = options.operationId || uuid();
    var stamped = stampItems(items, operationId);
    return withOperationLock('add:' + operationId, function () {
      return Promise.resolve().then(function () { return options.ownedPreflight ? consumeOwnedCart(options.ownedPreflight) : getCart(); }).then(function (before) {
        var existing = matchingAttempt(before, stamped, operationId);
        if (existing.complete) return { added: true, alreadyInCart: true, operationId: operationId, cart: before };
        if (existing.owned.length) throw cartError('DIY_CART_PARTIAL', 'Your selection is partly in your bag. Open your bag to review it before retrying.', operationId);
        if (isCancelled(operationId)) throw cartError('DIY_CART_CANCELLED', 'This purchase was cancelled. Please choose your selection again.', operationId);
        var prior = readOperation(OP_PREFIX + 'receipt:' + operationId);
        if (prior && prior.status === 'uncertain') throw cartError('DIY_CART_UNCERTAIN', 'We could not confirm the earlier add. Your selection is saved; review your bag before trying again.', operationId);
        writeOperation(OP_PREFIX + 'receipt:' + operationId, { status: 'uncertain' });
        var definiteRejection = false;
        return postJson(cartRoute('cart/add.js'), { items: stamped }).then(function (response) {
          definiteRejection = response.status >= 400 && response.status < 500;
        }, function () {}).then(function () { return options.ownedPreflight ? readOwnedVerifiedCart(false) : getCart(); }).then(function (fresh) {
          var result = matchingAttempt(fresh, stamped, operationId);
          if (result.complete && !isCancelled(operationId)) {
            writeOperation(OP_PREFIX + 'receipt:' + operationId, { status: 'complete' });
            return { added: true, operationId: operationId, cart: fresh };
          }
          if (result.owned.length) throw cartError('DIY_CART_PARTIAL', 'Your selection is partly in your bag. Please review your bag; we have not added it again.', operationId);
          if (definiteRejection) {
            writeOperation(OP_PREFIX + 'receipt:' + operationId, { status: 'rejected' });
            throw cartError('DIY_CART_REJECTED', 'This selection could not be added. Please check availability and try again.', operationId);
          }
          throw cartError('DIY_CART_UNCERTAIN', 'We could not confirm the add. Your selection is saved; please review your bag.', operationId);
        }, function (error) {
          if (error && error.code) throw error;
          throw cartError('DIY_CART_UNCERTAIN', 'We could not verify your bag after the add. Your selection is saved.', operationId);
        });
      });
    });
  }

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.floor(Math.random() * 16);
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  function money(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }); }
  function getCart() { return fetch(cartRoute('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }).then(function (r) { if (!r.ok) throw new Error('cart read ' + r.status); return r.json(); }).then(function (cart) { if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response'); return cart; }); }

  // Display metadata only. Pending identity and native checkout validation stay separate.
  function pendingDisplayProps(d, realPrice) {
    try {
      var dc = window.DiyonaCurrency;
      var ctx = dc && typeof dc.context === 'function' ? dc.context() : (window.DiyonaMoneyContext || {});
      var currency = String(ctx.currency || '').toUpperCase(), country = String(ctx.country || '').toUpperCase();
      if (!/^[A-Z]{3}$/.test(currency) || !/^[A-Z]{2}$/.test(country)) return {};
      var quote = d && d._nativeQuote, amount, source = 'usd_estimate';
      if (quote && dc && typeof dc.matchesQuote === 'function' && dc.matchesQuote(quote) &&
          d.sku && quote.sku === d.sku && quote.country === country && quote.price && quote.price.currencyCode === currency &&
          isFinite(Number(quote.price.amount)) && Number(quote.price.amount) > 0) {
        amount = Number(quote.price.amount); source = 'native_quote';
      } else if (dc && typeof dc.convert === 'function') {
        amount = dc.convert(realPrice);
      } else if (currency === 'USD') {
        amount = realPrice;
      }
      if (!isFinite(amount) || !(amount > 0)) return {};
      // Match cart line display precision before the estimate contributes to a total.
      var digits = Math.min(2, new Intl.NumberFormat('en', {style:'currency',currency:currency}).resolvedOptions().maximumFractionDigits);
      amount = Number(new Intl.NumberFormat('en', {useGrouping:false,minimumFractionDigits:digits,maximumFractionDigits:digits}).format(amount));
      if (!isFinite(amount) || !(amount > 0)) return {};
      return { '_display_price': String(amount), '_display_currency': currency, '_display_country': country,
        '_display_price_source': source, '_display_price_version': '1' };
    } catch (e) { return {}; }
  }

  /* ---- Build the Step-3 ring line items (called by complete-ring ATC) ----
     Ring size is OPTIONAL at step 3: sizeLabel null omits the Ring Size property
     and the cart page hard-blocks checkout until the shopper picks one there. */
  function buildRingLineItems(opts) {
    var d = opts.diamond, setting = opts.setting, engrave = opts.engrave, shipBy = opts.shipBy;
    // Prefer the pre-formatted sizeLabel ('US 7' or null); legacy callers pass size.
    var sizeLabel = (opts.sizeLabel !== undefined) ? opts.sizeLabel : (opts.size ? 'US ' + opts.size : null);
    var pid = uuid();
    var idem = uuid();
    var diamondSummary = d.carat + 'ct ' + d.color + ' ' + d.clarity + ' ' + d.shape;
    // Field-tolerant: the ring-builder cookie diamond uses price/image/certificate;
    // the Supabase row uses markup_price/price_usd/image_url/certificate_number.
    var realPrice = Number(d.markup_price || d.price_usd || d.price || 0);
    var imageUrl = d.image_url || d.image || '';
    var certNum = d.certificate_number || d.certificate || '';

    var diamondProps = {
      // visible
      'Diamond': diamondSummary,
      'Certificate': ((d.lab || 'IGI') + ' ' + certNum).trim(),
      'Paired Setting': setting.title,
      'Ship by': shipBy,
      // hidden contract
      '_ring_builder': 'true',
      '_ring_type': 'Ring',
      '_pending_diamond_id': pid,
      '_idempotency_key': idem,
      '_diamond_sku': d.sku,
      '_real_price': realPrice.toFixed(2),
      '_image_url': imageUrl,
      '_shape': d.shape || '', '_carat': String(d.carat || ''), '_color': d.color || '',
      '_clarity': d.clarity || '', '_cut': d.cut || '', '_lab': d.lab || '',
      '_certificate_number': certNum, '_max_delivery_days': String(d.max_delivery_days || '')
    };
    var settingProps = {
      '_ring_builder': 'true',
      'Paired Diamond': diamondSummary,
      'Diamond SKU': d.sku,
      'Ship by': shipBy
    };
    Object.assign(diamondProps, pendingDisplayProps(d, realPrice));
    if (opts.operationId) { diamondProps[OP_PROP] = opts.operationId; settingProps[OP_PROP] = opts.operationId; }
    if (sizeLabel) { diamondProps['Ring Size'] = sizeLabel; settingProps['Ring Size'] = sizeLabel; }
    if (engrave) { diamondProps['_engraving'] = engrave; settingProps['Custom Engraving'] = engrave; }

    var items = [
      DiyonaInventory.line({ id: SENTINEL_VARIANT, quantity: 1, properties: diamondProps }, d),
      { id: setting.variantId, quantity: 1, properties: settingProps }
    ];
    return { items: items, pendingId: pid };
  }

  function ringPayload(opts, operationId) {
    return DiyonaInventory.prepare(opts.diamond, "Ring").then(function(current) {
    opts = Object.assign({}, opts, { diamond: current });
    var items = buildRingLineItems(Object.assign({}, opts, { operationId: operationId })).items;
    if (!opts.engrave) return Promise.resolve(items);
    return fetch(cartRoute('products/engraving-fee.js'), { credentials: 'same-origin', cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw new Error('Engraving lookup ' + r.status);
      return r.json();
    }).then(function (fee) {
      var variant = fee && Array.isArray(fee.variants) && fee.variants.filter(function (v) { return v && Number(v.id) > 0 && v.available !== false; })[0];
      if (!variant) throw new Error('Engraving unavailable');
      items.push({ id: variant.id, quantity: 1, properties: { '_ring_builder': 'true', 'Engraving Text': opts.engrave, '_diamond_sku': opts.diamond.sku } });
      return items;
    }).catch(function () { throw cartError('DIY_FEE_UNAVAILABLE', 'Engraving could not be confirmed. Your text is saved; please retry.'); });
      });
  }
  function optimisticAddRingLegacy(opts) {
    return withOperationLock('purchase:' + opts.diamond.sku, function () {
      if (opts.diamond.sku) delete removeIntents[opts.diamond.sku];
      var operationId = purchaseAttempt(opts).value.id;
      return getCart().then(function (cart) {
        var existing = findDiamondInCart(cart, opts.diamond.sku);
        if (existing && !existing.ambiguous && sameRingSelection(cart, existing, opts)) return { added: true, alreadyInCart: true, cart: cart };
        if (existing) throw cartError('DIY_CART_PARTIAL', 'This diamond is already in your bag. Review or update that selection before adding it again.');
        return ringPayload(opts, operationId).then(function (items) { return addItemsVerified(items, { operationId: operationId }); });
      });
    });
  }

  function sameRingSelection(cart, found, opts) {
    if (!found || found.type !== 'ring') return false;
    var setting = cart.items.filter(function (line) { return line.key === found.settingKey; })[0];
    var diamond = cart.items.filter(function (line) { return line.key === found.diamondKey; })[0];
    if (ownedSelection(opts.diamond) && !ownedSelectionMatches(diamond, opts.diamond)) return false;
    var fee = cart.items.filter(function (line) { return line.key === found.feeKey; })[0];
    var size = opts.sizeLabel === undefined ? (opts.size ? 'US ' + opts.size : '') : (opts.sizeLabel || '');
    return !!(setting && diamond && setting.quantity === 1 && diamond.quantity === 1 && Number(setting.variant_id) === Number(opts.setting.variantId) &&
      String(setting.properties['Ring Size'] || '') === String(size) && String(setting.properties['Custom Engraving'] || '') === String(opts.engrave || '') &&
      String((diamond.properties || {})['Ring Size'] || '') === String(size) &&
      (Number(diamond.variant_id) === SENTINEL_VARIANT || verifiedDiamond(diamond, opts.diamond.sku)) &&
      (!opts.engrave || (fee && fee.quantity === 1 && fee.properties['Engraving Text'] === opts.engrave)));
  }
  // Own the entire replacement outcome, including removal verification. Unknown
  // outcomes never trigger blind fallback/restore writes.
  function replaceRingLegacy(opts) {
    return withOperationLock('purchase:' + opts.diamond.sku, function () {
      return getCart().then(function (cart) {
        var existing = findDiamondInCart(cart, opts.diamond.sku);
        if (existing && existing.ambiguous) throw cartError('DIY_CART_PARTIAL', 'This ring needs review in your bag before it can be changed.');
        if (sameRingSelection(cart, existing, opts)) return { added: true, alreadyInCart: true, cart: cart };
        var operationId = purchaseAttempt(opts).value.id;
        // Resolve every required component before removing the previous selection.
        return ringPayload(opts, operationId).then(function (items) {
          if (!existing) return addItemsVerified(items, { operationId: operationId });
          var saved = cart.items.filter(function (line) { return existing.keys.indexOf(line.key) !== -1; }).map(function (line) {
            return { id: line.variant_id, quantity: line.quantity, properties: Object.assign({}, line.properties || {}) };
          });
          var journalId = uuid();
          writeOperation(OP_PREFIX + 'replacement:' + journalId, { status: 'removing', saved: saved });
          return removeCartLines(existing.keys).then(function (after) {
            if (findDiamondInCart(after, opts.diamond.sku)) throw cartError('DIY_CART_UNCERTAIN', 'We could not verify the previous ring was removed. Your selection is saved.');
            // Cancellation of the old operation must not cancel the replacement.
            if (isCancelled(operationId)) operationId = uuid();
            delete removeIntents[opts.diamond.sku];
            return addItemsVerified(items, { operationId: operationId }).catch(function (error) {
              if (!error || error.code !== 'DIY_CART_REJECTED') throw error;
              return getCart().then(function (current) {
                if (findDiamondInCart(current, opts.diamond.sku)) throw cartError('DIY_CART_PARTIAL', 'Your bag changed while replacing the ring. Please review it before retrying.');
                var restoreId = uuid();
                saved.forEach(function (item) { item.properties[OP_PROP] = restoreId; });
                return addItemsVerified(saved, { operationId: restoreId }).then(function () {
                  error.message = 'The replacement was rejected. Your previous ring was restored; please review your bag.';
                  throw error;
                });
              });
            });
          }).then(function (result) { writeOperation(OP_PREFIX + 'replacement:' + journalId, { status: 'complete' }); return result; });
        });
      });
    });
  }

  /* ---- Build a single LOOSE diamond line (called by diamond-detail "Buy Loose") ---- */
  function buildLooseLineItem(d, shipBy) {
    var pid = uuid();
    var idem = uuid();
    var diamondSummary = d.carat + 'ct ' + d.color + ' ' + d.clarity + ' ' + d.shape;
    // Field-tolerant: Supabase row uses markup_price/price_usd/image_url/certificate_number.
    var realPrice = Number(d.markup_price || d.price_usd || d.price || 0);
    var imageUrl = d.image_url || d.image || '';
    var certNum = d.certificate_number || d.certificate || '';
    var props = {
      // visible
      'Diamond': diamondSummary,
      'Certificate': ((d.lab || '') + ' ' + certNum).trim(),
      'Ship By': shipBy,
      // hidden contract
      '_pending_diamond_id': pid,
      '_idempotency_key': idem,
      '_pending_loose': 'true',     // cart-item.liquid renders this as bundle_type 'loose', not 'diamond'
      '_ring_type': 'Loose',        // reconcile() -> create-product?type=Loose
      '_diamond_sku': d.sku,
      '_real_price': realPrice.toFixed(2),
      '_image_url': imageUrl,
      '_shape': d.shape || '', '_carat': String(d.carat || ''), '_color': d.color || '',
      '_clarity': d.clarity || '', '_cut': d.cut || '', '_lab': d.lab || '',
      '_certificate_number': certNum, '_max_delivery_days': String(d.max_delivery_days || '')
    };
    Object.assign(props, pendingDisplayProps(d, realPrice));
    return DiyonaInventory.line({ id: SENTINEL_VARIANT, quantity: 1, properties: props }, d);
  }

  // Optimistic loose add: dedupe against the current cart by SKU, else add a $0 sentinel loose
  // line and let /cart reconcile it in the background. Resolves fast (no create-product wait),
  // so the caller can redirect to /cart immediately. The diamond renders from its line properties.
  function optimisticAddLooseLegacy(d, shipBy) {
    if (d.sku) delete removeIntents[d.sku];
    var attempt = purchaseAttempt({ diamond: d, operationId: d.operationId });
    return withOperationLock('purchase:' + d.sku, function () {
      return getCart().then(function (cart) {
        if ((cart.items || []).some(function (line) { return matchesDiamondSku(line, d.sku); })) return { alreadyInCart: true, cart: cart };
        return DiyonaInventory.prepare(d, "Loose").then(function(current) { return addItemsVerified([buildLooseLineItem(current, shipBy)], { operationId: attempt.value.id }); });
      });
    });
  }

  /* ---- ATC overlay animation (minimal; safe no-ops if markup absent) ---- */
  function showAtcAnimation() { var o = document.getElementById('diy-atc-overlay'); if (o) o.classList.add('is-active'); }
  function hideAtcAnimation() { var o = document.getElementById('diy-atc-overlay'); if (o) o.classList.remove('is-active'); }

  /* ================= /cart PAGE: deferred checkout + reconcile ================= */
  // The "Continue to Checkout" button looks/behaves 100% normal. If a shopper clicks it BEFORE the
  // diamond's real product has been created+added, we don't gray it out — we show an in-button spinner
  // and auto-proceed to checkout the instant the product lands. No grayed/disabled state.
  var pendingActive = false;  // a sentinel diamond is still being created/swapped
  var wantsCheckout = false;  // shopper clicked checkout during the pending window
  var failedActive = false;
  var reconcileRunning = null;
  var checkoutRunning = false;
  var checkoutPermit = null;
  var checkoutBlocked = true;
  var removeIntents = {};
  var validationSequence = 0;
  var externalMutationSequence = 0;
  var cartMutations = {};
  var renderActive = false;
  var renderNeedsRefresh = false;
  var legacyCartRefreshRequested = false;
  function cartWriteActive() { return Object.keys(cartMutations).length > 0 || renderActive; }
  function cartContext() {
    var money = window.DiyonaMoneyContext || {}, shopify = window.Shopify || {};
    return [window.location.pathname, window.location.search, money.country, money.currency, shopify.currency && shopify.currency.active].join('|');
  }
  var arrivalSnapshot = null;
  var arrivalReadGeneration = 0;
  var arrivalKeyChanges = {};
  var arrivalCartRelevant = false;
  var arrivalSaving = false;
  // Prevent a second cart edit while an explicit checkout refresh is replacing properties.
  ['click', 'change', 'input', 'keydown', 'submit'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (!arrivalSaving) return;
      var target = e.target;
      if (target && target.closest && target.closest('#CartPageForm, .cart__drawer-form, .site-header__cart')) {
        e.preventDefault(); e.stopImmediatePropagation();
      }
    }, true);
  });
  // QtySelector caches its original key. Translate only keys changed by date saves,
  // before the theme's bubbling quantity handler runs; preserve quantity and wrapper.
  ['cart:quantity.cart-page', 'cart:quantity.cart-header', 'cart:quantity.cart-cart-drawer'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (!e.detail || !e.detail.length) return;
      if (arrivalSaving) { e.preventDefault(); e.stopImmediatePropagation(); return; }
      var key = e.detail[0], steps = 0;
      while (arrivalKeyChanges[key] && steps++ < 20) key = arrivalKeyChanges[key];
      e.detail[0] = key;
    }, true);
  });

  function checkoutBtn() { return document.querySelector('button[name="checkout"], .cart__checkout'); }

  function setGate(on) {
    pendingActive = on;
    // Surface pending state to CSS: accelerated wallet buttons (Apple Pay / Google Pay /
    // Shop Pay) bypass the click interceptor below — main-cart.liquid shields them with
    // pointer-events while this class is up, so a wallet can't buy a $0 sentinel line.
    document.documentElement.classList.toggle('diy-cart-pending', !!on);
    if (!on && wantsCheckout) {
      // Diamond is ready and the shopper already asked to check out — proceed now.
      wantsCheckout = false;
      var btn = checkoutBtn();
      if (btn) btn.click(); // Every attempt still validates the fresh server cart.
    }
  }

  // ---- rbfix v1: shared bundle-line classifiers (used by remove interceptor, reconcile guard, FIX 4) ----
  function isSettingLine(li) { var q = (li && li.properties) || {}; return q._ring_builder === 'true' && !!q['Paired Diamond']; }
  function isFeeLine(li) { var q = (li && li.properties) || {}; return q._ring_builder === 'true' && !!q['Engraving Text'] && !q['Paired Setting'] && !q['Paired Diamond']; }
  // Diamond-shaped lines only (excludes setting + engraving-fee lines, which can carry _diamond_sku too).
  function matchesDiamondSku(li, sku) {
    if (!sku || isSettingLine(li) || isFeeLine(li)) return false;
    return (li.properties && li.properties._diamond_sku === sku) || li.sku === sku;
  }

  // ---- Robust REMOVE during the pending window ----
  // While a sentinel diamond is still being created, the theme's native remove targets the sentinel
  // line key — a loose line removes via GET /cart/change?id=<key> (can land on a Shopify error page),
  // and the in-flight reconcile would re-add the diamond after removal ("won't actually remove").
  // Intercept "Remove" / "Remove ring" here: resolve the line(s) from the CURRENT cart (diamonds by
  // SKU, the setting by its pairing attributes — stale DOM keys never error), remove via
  // /cart/update.js, then reload — which also tears down the in-flight reconcile.
  // INVARIANT (rbfix v1): never POST a removal that deletes only one member of a currently-consistent
  // bundle. If a member exists in the live cart but can't be confidently resolved, reload instead.
  document.addEventListener('click', function (e) {
    if (!pendingActive) return;
    var bundleRemove = e.target && e.target.closest && e.target.closest('.bundle__remove');
    var lineRemove = e.target && e.target.closest && e.target.closest('.cart__item .cart__remove a');
    if (!bundleRemove && !lineRemove) return;
    e.preventDefault(); e.stopImmediatePropagation();
    var removalRoot = bundleRemove ? bundleRemove.closest('.bundle') : lineRemove.closest('.cart__item');
    var removalDiamond = removalRoot && (removalRoot.matches('.cart__item') ? removalRoot : removalRoot.querySelector('[data-diamond-sku], [data-setting-diamond-sku]'));
    var removalSku = removalDiamond && (removalDiamond.getAttribute('data-diamond-sku') || removalDiamond.getAttribute('data-setting-diamond-sku'));
    if (removalSku) removeIntents[removalSku] = true;
    getCart().then(function (cart) {
      var items = cart.items || [];
      var updates = {};
      function findByKey(key) { if (!key) return null; for (var i = 0; i < items.length; i++) { if (items[i].key === key) return items[i]; } return null; }
      function findDiamond(sku, domKey) {
        if (!sku) return null;
        var m = items.filter(function (li) { return matchesDiamondSku(li, sku); });
        if (m.length > 1 && domKey) { for (var i = 0; i < m.length; i++) { if (m[i].key === domKey) return m[i]; } }
        return m[0] || null;
      }
      if (bundleRemove) {
        var bundle = bundleRemove.closest('.bundle');
        var settingEl = bundle && bundle.querySelector('.cart__item[data-bundle-type="setting"]');
        var diamondEl = bundle && bundle.querySelector('.cart__item[data-bundle-type="diamond"]');
        var sku = (diamondEl && diamondEl.getAttribute('data-diamond-sku')) || (settingEl && settingEl.getAttribute('data-setting-diamond-sku')) || null;
        if (diamondEl && !sku) { ttReload(); return; } // diamond card present but unresolvable
        var settingDomKey = settingEl && settingEl.getAttribute('data-key');
        // Setting: pairing attributes first, stale DOM key second, unique variant id last.
        var settingLine = null;
        var sMatches = sku ? items.filter(function (li) { return isSettingLine(li) && li.properties['Diamond SKU'] === sku; }) : [];
        if (sMatches.length === 1) settingLine = sMatches[0];
        else if (sMatches.length > 1) settingLine = sMatches.filter(function (li) { return li.key === settingDomKey; })[0] || null;
        if (!settingLine && !sMatches.length) {
          settingLine = findByKey(settingDomKey);
          if (!settingLine && settingDomKey) {
            var vid = parseInt(settingDomKey.split(':')[0], 10);
            var vMatches = vid ? items.filter(function (li) { return isSettingLine(li) && li.variant_id === vid; }) : [];
            if (vMatches.length === 1) settingLine = vMatches[0];
          }
        }
        var diamondLine = findDiamond(sku, diamondEl && diamondEl.getAttribute('data-key'));
        var diamondExists = sku ? items.some(function (li) { return matchesDiamondSku(li, sku); }) : false;
        if ((sMatches.length && !settingLine) || (diamondExists && !diamondLine) || (!settingLine && !diamondLine)) { ttReload(); return; }
        if (sku) removeIntents[sku] = true;
        if (settingLine) updates[settingLine.key] = 0;
        // During add-then-swap both representations may exist. An explicit Remove ring
        // removes both, scoped by this SKU, so a real line cannot survive as an orphan.
        items.forEach(function (li) { if (matchesDiamondSku(li, sku)) updates[li.key] = 0; });
        // Engraving fee scoped to THIS bundle: _diamond_sku stamp first, else exact Engraving Text
        // match on fee lines not stamped for another ring's diamond.
        var ownership = sku && findDiamondInCart(cart, sku);
        if (ownership && ownership.ambiguous) { showCartNotice(cart, 'This ring needs review before removal. Your selection is saved.'); return; }
        if (ownership && ownership.feeKey) updates[ownership.feeKey] = 0;
      } else {
        var it = lineRemove.closest('.cart__item[data-key]');
        var lineSku = it && it.getAttribute('data-diamond-sku');
        var domKey = it && it.getAttribute('data-key');
        var match = findDiamond(lineSku, domKey) || findByKey(domKey);
        if (!match) { ttReload(); return; }
        // A single-line remove must never split a currently-consistent bundle.
        var mp = match.properties || {};
        if (isSettingLine(match)) {
          var pairedSku = mp['Diamond SKU'];
          if (pairedSku && items.some(function (li) { return matchesDiamondSku(li, pairedSku); })) { ttReload(); return; }
        } else if (mp._ring_builder === 'true' && mp['Paired Setting']) {
          var mSku = mp._diamond_sku || match.sku;
          if (mSku && items.some(function (li) { return isSettingLine(li) && li.properties['Diamond SKU'] === mSku; })) { ttReload(); return; }
        }
        var removingSku = mp._diamond_sku || match.sku;
        if (removingSku) removeIntents[removingSku] = true;
        items.forEach(function (li) { if (matchesDiamondSku(li, removingSku)) updates[li.key] = 0; });
        updates[match.key] = 0;
      }
      if (!Object.keys(updates).length) { ttReload(); return; }
      return removeCartLines(Object.keys(updates)).then(function () { ttReload(); });
    }).catch(function () { ttReload(); });
  }, true);

  function pendingLines(cart) {
    // A malformed/legacy sentinel is still a $0 placeholder and must never check out.
    return (cart.items || []).filter(function (li) { return Number(li.variant_id) === SENTINEL_VARIANT || (li.properties && li.properties[PENDING_PROP]); });
  }

  /* ================= swap v2: idempotent, outcome-aware reconcile =================
     Each pending (sentinel) line resolves to exactly one outcome:
       { swapped: true }    real diamond line is in the cart, sentinel removed
       { blocked: true }    diamond cannot be confirmed -> preserve selection, offer recovery
       { cancelled: true }  shopper removed the ring mid-swap
       { retry: true }      TEMPORARY failure (network / 5xx) -> keep the gate, retry
     Decisions are always made from a fresh /cart.js read, never from a status code alone:
     Shopify can answer 422 and still have merged the line, and an earlier swap may have
     added the real product without ever deleting its placeholder. */
  function postJson(url, body, source) {
    var mutationId = uuid();
    notifyMutation('start', mutationId, source);
    return Promise.resolve().then(function () {
      return fetch(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    }).then(function (response) { notifyMutation('end', mutationId, source); return response; }, function (error) { notifyMutation('end', mutationId, source); throw error; });
  }
  function delay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function removeLine(key) { return postJson(cartRoute('cart/change.js'), { id: key, quantity: 0 }).catch(function () { return null; }); }
  function lineInCart(cart, key) { return (cart.items || []).some(function (li) { return li.key === key; }); }
  function verifiedDiamond(line, sku) {
    return !!(line && ownedNativeIdentity(line, line.properties) && !isSettingLine(line) && !isFeeLine(line) && Number(line.variant_id) > 0 && Number(line.variant_id) !== SENTINEL_VARIANT &&
      !(line.properties || {})[PENDING_PROP] && line.sku === sku && Number(line.quantity) === 1);
  }
  function findSwappedLine(cart, sku, sentinelKey, pending, variantId) {
    var expected = pending && pending.properties || {}, operationId = pending && operationOf(pending);
    var matches = (cart.items || []).filter(function (li) {
      var p = li.properties || {};
      if (li.key === sentinelKey || !verifiedDiamond(li, sku) || (variantId && Number(li.variant_id) !== Number(variantId))) return false;
      if (pending && (p._ring_type || (p['Paired Setting'] ? 'Ring' : 'Loose')) !== (expected._ring_type || 'Ring')) return false;
      // New operations require exact ownership; legacy settled lines may predate it.
      if (operationId && p[OP_PROP] && p[OP_PROP] !== operationId) return false;
      if (expected[OP_PROP] && p[OP_PROP] !== operationId) return false;
      if (pending && String(p['Paired Setting'] || '') !== String(expected['Paired Setting'] || '')) return false;
      return true;
    });
    return matches.length === 1 ? matches[0] : null;
  }
  function createProduct(sku, rtype) {
    return fetch(API_BASE + '/create-product?sku=' + encodeURIComponent(sku) + '&type=' + encodeURIComponent(rtype), { cache: 'no-store' }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        var id = Number(data && data.variant_id);
        if (r.ok && Number.isSafeInteger(id) && id > 0 && id !== SENTINEL_VARIANT) return { variantId: id };
        if (r.status === 404) return { unavailable: true, reason: data.error || 'Diamond unavailable' };
        return { retry: true, reason: 'create-product ' + r.status };
      });
    }).catch(function (e) { return { retry: true, reason: String(e && e.message || e) }; });
  }
  function pendingFor(cart, line) {
    var id = operationOf(line);
    return pendingLines(cart).filter(function (candidate) { return candidate.key === line.key || (id && operationOf(candidate) === id); })[0] || null;
  }
  function removeCancelledOperation(cart, operationId) {
    var keys = (cart.items || []).filter(function (li) { return operationOf(li) === operationId; }).map(function (li) { return li.key; });
    return removeCartLines(keys);
  }
  // Reconcile uses server SKU, operation ownership, and a fresh read before AND
  // after each write. Unknown write outcomes are read back, never blindly replayed.
  function reconcile(line) {
    var p = line.properties || {}, sku = p._diamond_sku, operationId = operationOf(line);
    if (!sku || Number(line.quantity) !== 1) return Promise.resolve({ blocked: true, reason: 'Invalid pending diamond' });
    return withOperationLock('purchase:' + sku, function () {
      function finish(pending, dedup) {
        return removeLine(pending.key).then(getCart).then(function (fresh) {
          if (pendingFor(fresh, pending)) return { retry: true, reason: 'remove sentinel failed' };
          if (!findSwappedLine(fresh, sku, pending.key, pending)) return { blocked: true, reason: 'Confirmed diamond changed' };
          return { swapped: true, dedup: !!dedup };
        });
      }
      return getCart().then(function (cart) {
        var pending = pendingFor(cart, line);
        if (isCancelled(operationId) || removeIntents[sku]) return removeCancelledOperation(cart, operationId).then(function () { return { cancelled: true }; });
        if (!pending) return { cancelled: true };
        if (findSwappedLine(cart, sku, pending.key, pending)) return finish(pending, true);
        if (cart.items.some(function (li) { return li.key !== pending.key && matchesDiamondSku(li, sku); })) return { blocked: true, reason: 'This diamond has another selection in your bag. Review it before confirming.' };
        var receiptKey = OP_PREFIX + 'swap:' + operationId, prior = readOperation(receiptKey);
        if (prior && prior.status === 'uncertain') return { blocked: true, reason: 'Earlier diamond add could not be confirmed. Review your bag before retrying.' };
        return createProduct(sku, p._ring_type || 'Ring').then(function (res) {
          return res.unavailable ? delay(1500).then(function () { return createProduct(sku, p._ring_type || 'Ring'); }) : res;
        }).then(function (res) {
          if (res.retry) return res;
          if (res.unavailable) return { blocked: true, reason: res.reason };
          return getCart().then(function (beforeAdd) {
            pending = pendingFor(beforeAdd, line);
            if (isCancelled(operationId) || removeIntents[sku]) return removeCancelledOperation(beforeAdd, operationId).then(function () { return { cancelled: true }; });
            if (!pending) return { cancelled: true };
            if (findSwappedLine(beforeAdd, sku, pending.key, pending, res.variantId)) return finish(pending, true);
            if (beforeAdd.items.some(function (li) { return li.key !== pending.key && matchesDiamondSku(li, sku); })) return { blocked: true, reason: 'This diamond selection changed while being confirmed.' };
            var newProps = {};
            Object.keys(pending.properties || {}).forEach(function (key) { if (key !== PENDING_PROP && key !== '_idempotency_key') newProps[key] = pending.properties[key]; });
            newProps[OP_PROP] = operationId;
            newProps._optimistic_swapped = '1';
            writeOperation(receiptKey, { status: 'uncertain', variantId: res.variantId });
            var responseStatus = 0;
            return postJson(cartRoute('cart/add.js'), { items: [{ id: res.variantId, quantity: 1, properties: newProps }] }).then(function (response) { responseStatus = response.status; }, function () {}).then(getCart).then(function (fresh) {
              if (isCancelled(operationId) || removeIntents[sku]) return removeCancelledOperation(fresh, operationId).then(function () { return { cancelled: true }; });
              var real = findSwappedLine(fresh, sku, pending.key, pending, res.variantId);
              if (!real) {
                var wrong = fresh.items.filter(function (li) { return li.properties && li.properties[OP_PROP] === operationId && !li.properties[PENDING_PROP] && !isSettingLine(li) && !isFeeLine(li); });
                if (wrong.length) {
                  // Remove only the unexpected line from our add; preserve the selection.
                  return Promise.all(wrong.map(function (li) { return removeLine(li.key); })).then(function () { return { blocked: true, reason: 'Diamond identity could not be verified' }; });
                }
                if (responseStatus >= 400 && responseStatus < 500) {
                  writeOperation(receiptKey, { status: 'rejected' });
                  return { blocked: true, reason: 'Diamond could not be added (' + responseStatus + ')' };
                }
                return { blocked: true, reason: 'Diamond add outcome is uncertain. Your selection is saved.' };
              }
              writeOperation(receiptKey, { status: 'complete' });
              return finish(pending, false);
            });
          });
        });
      });
    }).catch(function (error) { return { retry: true, reason: String(error && error.message || error) }; });
  }

  // Bounded retry for TEMPORARY failures only (network / 5xx). Terminal outcomes never retry.
  var RETRY_DELAYS = [1500, 4000, 8000];
  function reconcileWithRetry(line, attempt) {
    attempt = attempt || 0;
    return reconcile(line).then(function (res) {
      if (!res || !res.retry || attempt >= RETRY_DELAYS.length) return res;
      console.warn('[optimistic-cart] swap retry ' + (attempt + 1) + ' (' + res.reason + ')');
      return delay(RETRY_DELAYS[attempt]).then(function () { return reconcileWithRetry(line, attempt + 1); });
    });
  }

  function silentFinalize() {
    function legacyRefresh() {
      // Legacy pages cannot safely retain placeholder prices/keys after settlement.
      var active = document.activeElement;
      if (active && active.matches && active.matches('input, textarea, select')) return Promise.reject(cartError('DIY_CART_EDITING', 'Finish editing your bag, then retry.'));
      // TT navigation keeps this runtime alive, so use a full reload here too.
      if (!legacyCartRefreshRequested) { legacyCartRefreshRequested = true; window.location.reload(); }
      return Promise.reject(cartError('DIY_CART_REFRESHING', 'Refreshing your confirmed bag.'));
    }
    if (window.DiyonaCartRender && window.DiyonaCartRender.refresh) return window.DiyonaCartRender.refresh().catch(function (error) {
      if (error && error.code === 'DIY_CART_LEGACY_CONTRACT') return legacyRefresh();
      throw error;
    });
    return legacyRefresh();
  }

  /* ================= Complete-cart validation and explicit recovery ================= */
  function mismatchedSettings(items) {
    return items.filter(function (li) {
      if (!isSettingLine(li)) return false;
      var sku = li.properties['Diamond SKU'];
      return !sku || !items.some(function (cand) { return matchesDiamondSku(cand, sku); });
    });
  }
  function orphanDiamonds(items) {
    return items.filter(function (li) {
      var p = li.properties || {};
      if (p._ring_builder !== 'true' || !p['Paired Setting']) return false;
      var sku = p._diamond_sku || li.sku;
      return !sku || !items.some(function (setting) { return isSettingLine(setting) && setting.properties['Diamond SKU'] === sku; });
    });
  }
  function validateCheckoutCart(cart) {
    var items = cart.items || [];
    var pending = pendingLines(cart);
    var incomplete = mismatchedSettings(items).concat(orphanDiamonds(items));
    items.forEach(function (line) {
      var p = line.properties || {};
      if (p._ring_builder !== 'true' && !p._pending_loose && !p._optimistic_swapped && p._inventory_source !== 'owned') return;
      var sku = p._diamond_sku || p['Diamond SKU'];
      if (sku && !isSettingLine(line) && !isFeeLine(line) && Number(line.variant_id) !== SENTINEL_VARIANT && !verifiedDiamond(line, sku)) incomplete.push(line);
      if (isFeeLine(line)) {
        var feeOwners = items.filter(function (owner) { return isSettingLine(owner) && (p._diamond_sku ? owner.properties['Diamond SKU'] === p._diamond_sku : owner.properties['Custom Engraving'] === p['Engraving Text']); });
        if (line.quantity !== 1 || feeOwners.length !== 1 || feeOwners[0].properties['Custom Engraving'] !== p['Engraving Text']) incomplete.push(line);
      }
      if (!isSettingLine(line)) return;
      var diamonds = items.filter(function (candidate) { return matchesDiamondSku(candidate, sku) && (candidate.properties || {})['Paired Setting']; });
      var owner = findDiamondInCart(cart, sku);
      var sameTextOwners = p['Custom Engraving'] ? items.filter(function (candidate) { return isSettingLine(candidate) && candidate.properties['Custom Engraving'] === p['Custom Engraving']; }).length : 0;
      var compatibleFees = items.filter(function (candidate) { var fp = candidate.properties || {}; return isFeeLine(candidate) && (fp._diamond_sku === sku || (!fp._diamond_sku && p['Custom Engraving'] && fp['Engraving Text'] === p['Custom Engraving'] && sameTextOwners === 1)); });
      if (line.quantity !== 1 || diamonds.length !== 1 || !owner || owner.ambiguous ||
          (p['Custom Engraving'] && (!owner.feeKey || compatibleFees.length !== 1)) ||
          (diamonds.length === 1 && String((diamonds[0].properties || {})['Ring Size'] || '') !== String(p['Ring Size'] || ''))) incomplete.push(line);
    });
    var missingSize = ringSizeMissingLines(cart);
    return { ok: !pending.length && !incomplete.length && !missingSize.length, pending: pending, incomplete: incomplete, missingSize: missingSize };
  }
  function sizeSaveActive() { return !!(window.DiyonaCartSizeSaving && window.DiyonaCartSizeSaving()); }
  function sizeGateActive() { var form = document.getElementById('CartPageForm'); return !!(form && form.classList.contains('is-size-gated')); }
  function setCheckoutBlocked(on) {
    checkoutBlocked = !!on || cartWriteActive() || pendingActive || renderNeedsRefresh;
    document.documentElement.classList.toggle('diy-cart-blocked', checkoutBlocked);
  }
  function clearCartNotice() {
    var n = document.getElementById('diy-bundle-notice');
    if (n && n.parentNode) n.parentNode.removeChild(n);
  }
  function recoveryAction(sku, key, change, button) {
    button.disabled = true;
    button.textContent = change ? 'Opening diamond selection…' : 'Removing…';
    // Finish an in-flight add before the shopper's explicit removal; otherwise it could re-add.
    Promise.resolve(reconcileRunning).then(getCart).then(function (cart) {
      var match = sku ? findDiamondInCart(cart, sku) : null;
      var line = (cart.items || []).filter(function (li) { return li.key === key; })[0];
      var keys = match ? match.keys : (line ? [line.key] : []);
      var setting = (cart.items || []).filter(function (li) { return isSettingLine(li) && ((sku && li.properties['Diamond SKU'] === sku) || li.key === key); })[0];
      var dest = setting && /^\/products\//.test(setting.url || '') ? setting.url : '/pages/diamond-search';
      return removeCartLines(keys).then(getCart).then(function (fresh) {
        if (keys.some(function (k) { return lineInCart(fresh, k); }) || (sku && findDiamondInCart(fresh, sku))) throw new Error('Removal not confirmed');
        if (!change) { ttReload(); return; }
        // Changing is an explicit replacement: remove only this ring, then restore its setting
        // configuration with no selected diamond. Other cart lines and saved builds stay intact.
        if (setting && setting.product_id) {
          try {
            ['diyCfg:', 'diyCfg.v2:'].forEach(function (prefix) {
              var stateKey = prefix + setting.product_id;
              var saved = JSON.parse(sessionStorage.getItem(stateKey) || 'null');
              if (saved) { saved.diamond = null; saved.step = 2; saved.browse = true; saved.dopen = true; sessionStorage.setItem(stateKey, JSON.stringify(saved)); }
            });
          } catch (e) {}
        }
        if (window.RingBuilder && window.RingBuilder.getDiamond && window.RingBuilder.clearDiamond) {
          var chosen = window.RingBuilder.getDiamond();
          if (chosen && chosen.sku === sku) window.RingBuilder.clearDiamond();
        }
        window.location.href = dest;
      });
    }).catch(function (e) {
      console.warn('[optimistic-cart] recovery failed', e);
      button.disabled = false;
      button.textContent = change ? 'Choose another diamond' : 'Remove ring/item';
      showCartNotice(null, 'We could not update your bag. Your selection is saved. Please retry.');
    });
  }
  function showCartNotice(cart, message) {
    clearCartNotice();
    var n = document.createElement('div');
    n.id = 'diy-bundle-notice';
    n.setAttribute('role', 'alert');
    n.style.cssText = 'margin:12px 0 0;padding:12px 14px;border:1px solid #b94a36;border-radius:3px;background:#faf1ef;color:#8f3a2a;font-size:13px;line-height:1.5;';
    var p = document.createElement('p');
    p.style.margin = '0 0 8px';
    p.textContent = message || 'We could not confirm your diamond. Your selection is saved. Retry, choose another diamond, or remove this ring before checking out.';
    n.appendChild(p);
    var retry = document.createElement('button');
    retry.type = 'button'; retry.textContent = 'Retry';
    retry.style.cssText = 'text-decoration:underline;margin-right:16px;';
    retry.addEventListener('click', function () { retry.disabled = true; retry.textContent = 'Retrying…'; initCartPage(); });
    n.appendChild(retry);
    if (cart) {
      var state = validateCheckoutCart(cart), seen = {};
      state.pending.concat(state.incomplete).forEach(function (li) {
        var q = li.properties || {}, sku = q._diamond_sku || q['Diamond SKU'] || li.sku;
        var identity = sku || li.key;
        if (seen[identity]) return;
        seen[identity] = true;
        var row = document.createElement('div'); row.style.marginTop = '8px';
        var name = document.createElement('span'); name.textContent = (q.Diamond || q['Paired Diamond'] || li.product_title || 'Ring') + ': '; row.appendChild(name);
        [true, false].forEach(function (change) {
          var b = document.createElement('button'); b.type = 'button';
          b.textContent = change ? 'Choose another diamond' : 'Remove ring/item';
          b.style.cssText = 'text-decoration:underline;margin-right:16px;';
          b.addEventListener('click', function () { recoveryAction(sku, li.key, change, b); });
          row.appendChild(b);
        });
        n.appendChild(row);
      });
    }
    var host = document.querySelector('#CartPageForm .cart__checkout-wrapper') || checkoutBtn();
    if (host && host.parentNode) host.parentNode.insertBefore(n, host.nextSibling);
  }
  /* Refresh saved ring dates only on the shopper's explicit checkout attempt.
     Ship by is the production deadline consumed by production software.
     Arrives by includes transit. Keep legacy keys and preserve that distinction. */
  function readArrivalSnapshot(doc) {
    var node = doc.querySelector('[data-diy-cart-arrival]');
    if (!node) return null;
    try {
      var data = JSON.parse(node.textContent);
      if (!data || typeof data.ship_date !== 'string' || !data.ship_date.trim() || typeof data.arrival_date !== 'string' || !data.arrival_date.trim() || !Array.isArray(data.lines) || !Number(data.generated)) return null;
      return data;
    } catch (e) { return null; }
  }
  function arrivalWindow(time) {
    var parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(time));
    var values = {};
    parts.forEach(function (part) { values[part.type] = part.value; });
    return values.year + '-' + values.month + '-' + values.day + ':' + (Number(values.hour) >= 15 ? 'after' : 'before');
  }
  function arrivalCartSignature(lines) {
    return JSON.stringify((lines || []).map(function (line) { return [line.key, line.quantity]; }).sort(function (a, b) { return a[0].localeCompare(b[0]); }));
  }
  function arrivalDateProperties(properties, snapshot, addShipBy) {
    var next = Object.assign({}, properties || {}), found = false;
    ['Ship by', 'Ship By'].forEach(function (key) {
      if (Object.prototype.hasOwnProperty.call(next, key)) { next[key] = snapshot.ship_date; found = true; }
    });
    if (Object.prototype.hasOwnProperty.call(next, 'Arrives by')) next['Arrives by'] = snapshot.arrival_date;
    if (!found && addShipBy !== false) next['Ship by'] = snapshot.ship_date;
    return next;
  }
  function arrivalPropertiesSignature(properties) {
    return JSON.stringify(Object.keys(properties || {}).sort().map(function (key) { return [key, properties[key]]; }));
  }
  function arrivalHasRelevantLines(cart, snapshot) {
    return (cart.items || []).some(function (line) {
      var p = line.properties || {};
      if (snapshot && snapshot.lines.some(function (entry) { return entry.eligible && entry.key === line.key; })) return true;
      if (p._ring_builder === 'true' && (p['Paired Diamond'] || p['Paired Setting'])) return true;
      if (/^engagement rings?$/i.test(line.product_type || '')) return true;
      return p._tiktok_flow === 'true' && !!(p['Ship by'] || p['Ship By'] || p['Arrives by']);
    });
  }
  function arrivalNeedsRefresh(cart) {
    var snapshot = arrivalSnapshot || readArrivalSnapshot(document);
    if (!arrivalHasRelevantLines(cart, snapshot)) return false;
    // A ring in a drawer has no server-rendered snapshot; normal checkout fetches it.
    if (!snapshot) return true;
    if (arrivalCartSignature(snapshot.lines) !== arrivalCartSignature(cart.items)) return true;
    if (!snapshot.lines.some(function (line) { return line.eligible; })) return false;
    if (snapshot.timeSensitive !== false && arrivalWindow(snapshot.generated * 1000) !== arrivalWindow(Date.now())) return true;
    return snapshot.lines.some(function (entry) {
      if (!entry.eligible) return false;
      var line = cart.items.filter(function (item) { return item.key === entry.key; })[0];
      return !line || arrivalPropertiesSignature(line.properties) !== arrivalPropertiesSignature(arrivalDateProperties(line.properties, snapshot, entry.addShipBy));
    });
  }
  // A property edit rekeys native lines without replacing the summary's arrival JSON.
  // Refresh only its read-only server evidence after a committed cart render.
  function refreshArrivalAfterRender(cart) {
    var generation = ++arrivalReadGeneration, context = cartContext();
    if (!validateCheckoutCart(cart).ok || !arrivalNeedsRefresh(cart)) return Promise.resolve();
    var expected = arrivalCartSignature(cart.items), money = window.DiyonaMoneyContext || {};
    var currency = typeof cart.currency === 'string' ? cart.currency : (cart.currency || {}).iso_code;
    var originalToken = cart.token ? String(cart.token) : '';
    function current() { return generation === arrivalReadGeneration && context === cartContext() && !cartWriteActive() && !arrivalSaving; }
    return (originalToken ? Promise.resolve(cart) : getCart()).then(function(before) {
        var beforeCurrency = typeof before.currency === 'string' ? before.currency : (before.currency || {}).iso_code;
        if (!current() || beforeCurrency !== currency || arrivalCartSignature(before.items) !== expected || !before.token) return null;
        originalToken = String(before.token);
        return fetch(cartRoute('cart?section_id=main-cart'), { credentials: 'same-origin', cache: 'no-store' });
      }).then(function(response) { if (!response) return null; if (!response.ok) throw new Error('Arrival snapshot read ' + response.status); return response.text(); })
      .then(function(html) {
        if (!html || !current()) return;
        var doc = new DOMParser().parseFromString(html, 'text/html'), snapshot = readArrivalSnapshot(doc);
        var node = doc.querySelector('[data-diy-cart-snapshot]'), native = node && JSON.parse(node.textContent);
        if (!snapshot || !native || native.schema !== 1 || !native.cart || !Array.isArray(native.cart.items) ||
            native.currency !== currency || (money.country && native.country !== money.country) ||
            arrivalCartSignature(snapshot.lines) !== expected || arrivalCartSignature(native.cart.items) !== expected ||
            (native.cart.token && String(native.cart.token) !== originalToken) ||
            (snapshot.timeSensitive !== false && arrivalWindow(snapshot.generated * 1000) !== arrivalWindow(Date.now()))) return;
        return getCart().then(function(fresh) {
          var freshCurrency = typeof fresh.currency === 'string' ? fresh.currency : (fresh.currency || {}).iso_code;
          if (!current() || freshCurrency !== currency || arrivalCartSignature(fresh.items) !== expected ||
              String(fresh.token || '') !== originalToken ||
              (snapshot.timeSensitive !== false && arrivalWindow(snapshot.generated * 1000) !== arrivalWindow(Date.now()))) return;
          arrivalSnapshot = snapshot;
          applyCartValidation(fresh); // Retains size, pending, date-property and all other checkout guards.
        });
      }).catch(function() { /* Keep the wallet gate closed; normal checkout can retry server verification. */ });
  }
  function refreshArrivalLineKey(oldKey, newKey) {
    if (oldKey === newKey) return;
    arrivalKeyChanges[oldKey] = newKey;
    ['data-key', 'data-id', 'data-setting-key', 'data-diamond-key', 'data-fee-key'].forEach(function (attribute) {
      document.querySelectorAll('[' + attribute + ']').forEach(function (node) {
        if (node.getAttribute(attribute) === oldKey) node.setAttribute(attribute, newKey);
      });
    });
    document.querySelectorAll('.cart__remove a').forEach(function (link) {
      var href = link.getAttribute('href') || '';
      if (href.indexOf(encodeURIComponent(oldKey)) >= 0) link.setAttribute('href', href.replace(encodeURIComponent(oldKey), encodeURIComponent(newKey)));
      else if (href.indexOf(oldKey) >= 0) link.setAttribute('href', href.replace(oldKey, encodeURIComponent(newKey)));
    });
    document.querySelectorAll('.js-qty__num').forEach(function (input) { if (input.id === 'cart_updates_' + oldKey) input.id = 'cart_updates_' + newKey; });
  }
  function arrivalOtherEditActive() {
    return sizeSaveActive() || !!document.querySelector('#CartPageForm .bundle__engraving.is-editing, #CartPageForm .bundle__engraving button:disabled, .js-qty__wrapper.is-loading');
  }
  function syncArrivalBeforeCheckout(cart) {
    ++arrivalReadGeneration;
    if (!arrivalHasRelevantLines(cart, arrivalSnapshot || readArrivalSnapshot(document))) return Promise.resolve(cart);
    if (arrivalOtherEditActive()) return Promise.reject(new Error('Cart details are still being saved'));
    arrivalSaving = true;
    setCheckoutBlocked(true);
    document.documentElement.classList.add('diy-arrival-saving');
    var snapshot, writeAttempted = false, expected = arrivalCartSignature(cart.items);
    var work = fetch(cartRoute('cart?section_id=main-cart'), { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) { if (!response.ok) throw new Error('Arrival date read ' + response.status); return response.text(); })
      .then(function (html) {
        snapshot = readArrivalSnapshot(new DOMParser().parseFromString(html, 'text/html'));
        if (!snapshot || arrivalCartSignature(snapshot.lines) !== expected) throw new Error('Your bag changed; please retry checkout');
        if (snapshot.timeSensitive !== false && arrivalWindow(snapshot.generated * 1000) !== arrivalWindow(Date.now())) throw new Error('Arrival date changed; please retry checkout');
        var sequence = Promise.resolve(cart);
        snapshot.lines.filter(function (entry) { return entry.eligible; }).forEach(function (entry) {
          sequence = sequence.then(getCart).then(function (current) {
            if (arrivalOtherEditActive() || !validateCheckoutCart(current).ok || arrivalCartSignature(current.items) !== expected) throw new Error('Your bag changed; please retry checkout');
            var line = current.items.filter(function (item) { return item.key === entry.key; })[0];
            if (!line) throw new Error('Ring line changed; please retry checkout');
            var properties = arrivalDateProperties(line.properties, snapshot, entry.addShipBy);
            if (arrivalPropertiesSignature(properties) === arrivalPropertiesSignature(line.properties)) return current;
            writeAttempted = true;
            return postJson(cartRoute('cart/change.js'), { id: line.key, quantity: line.quantity, properties: properties }, 'arrival')
              .then(function (response) { if (!response.ok) throw new Error('Arrival date save ' + response.status); return response.json(); })
              .then(function (updated) {
                if (!updated || !Array.isArray(updated.items)) throw new Error('Invalid cart response');
                var matches = updated.items.filter(function (item) { return item.variant_id === line.variant_id && item.quantity === line.quantity && arrivalPropertiesSignature(item.properties) === arrivalPropertiesSignature(properties); });
                if (matches.length !== 1) throw new Error('Arrival date save could not be confirmed');
                var updatedLine = matches[0];
                refreshArrivalLineKey(entry.key, updatedLine.key);
                entry.key = updatedLine.key;
                expected = arrivalCartSignature(snapshot.lines);
                if (arrivalCartSignature(updated.items) !== expected) throw new Error('Your bag changed; please retry checkout');
                return updated;
              });
          });
        });
        return sequence.then(getCart);
      }).then(function (fresh) {
        arrivalSnapshot = snapshot;
        if (arrivalCartSignature(fresh.items) !== expected || !validateCheckoutCart(fresh).ok || arrivalNeedsRefresh(fresh)) throw new Error('Arrival date refresh could not be confirmed');
        return fresh;
      });
    return work.then(function (fresh) {
      arrivalSaving = false;
      document.documentElement.classList.remove('diy-arrival-saving');
      return fresh;
    }, function (error) {
      arrivalSaving = false;
      document.documentElement.classList.remove('diy-arrival-saving');
      // A lost response can hide a successful re-key. Reload server truth before edits resume.
      if (writeAttempted) ttReload();
      throw error;
    });
  }

  function applyCartValidation(cart) {
    arrivalCartRelevant = arrivalHasRelevantLines(cart, arrivalSnapshot || readArrivalSnapshot(document));
    var state = validateCheckoutCart(cart);
    setCheckoutBlocked(!state.ok || sizeSaveActive() || sizeGateActive() || arrivalSaving || arrivalNeedsRefresh(cart));
    if (state.ok) clearCartNotice();
    else if (!state.pending.length && state.incomplete.length) showCartNotice(cart, 'This ring is missing its selected diamond or setting. Choose another diamond or remove the incomplete ring before checking out.');
    return state;
  }
  function checkoutAttempt(form, button) {
    if (checkoutRunning || sizeSaveActive() || cartWriteActive()) return;
    if (renderNeedsRefresh) { wantsCheckout = true; return initCartPage(); }
    checkoutRunning = true;
    var checkoutSequence = validationSequence, context = cartContext(), mutationSequence = externalMutationSequence;
    if (button && button.classList) button.classList.add('diy-loading');
    return getCart().then(function (cart) {
      if (checkoutSequence !== validationSequence || context !== cartContext() || (form && form.isConnected === false)) throw cartError('DIY_CART_CHANGED', 'Your bag changed. Please retry checkout.');
      var state = applyCartValidation(cart);
      if (state.pending.length) {
        if (!failedActive) { wantsCheckout = true; initCartPage(); }
        else showCartNotice(cart);
        return;
      }
      if (state.incomplete.length) return;
      if (state.missingSize.length) {
        var page = document.getElementById('CartPageForm');
        if (!page) { window.location.href = cartRoute('cart'); return; }
        page.classList.add('is-size-gated');
        showCartNotice(cart, 'Please select and save a ring size for every ring before checking out.');
        return;
      }
      // A size save may have started while the cart read was in flight.
      if (sizeSaveActive() || sizeGateActive() || cartWriteActive() || renderNeedsRefresh) {
        showCartNotice(cart, 'Please select and save a ring size for every ring before checking out.');
        return;
      }
      return syncArrivalBeforeCheckout(cart).then(function (fresh) {
      if (mutationSequence !== externalMutationSequence || context !== cartContext() || (form && form.isConnected === false)) throw cartError('DIY_CART_CHANGED', 'Your bag changed. Please retry checkout.');
      if (!applyCartValidation(fresh).ok || sizeSaveActive() || sizeGateActive() || cartWriteActive() || renderNeedsRefresh) return;
      clearCartNotice();
      if (form && form.requestSubmit) {
        checkoutPermit = form;
        try { form.requestSubmit(button && button.form === form ? button : undefined); }
        finally { checkoutPermit = null; }
      } else {
        // The cart has been checked; Shopify checkout still performs its own inventory checks.
        window.location.href = cartRoute('checkout');
      }
      });
    }).catch(function (e) {
      console.warn('[optimistic-cart] checkout validation failed', e);
      setCheckoutBlocked(true);
      showCartNotice(null, 'We could not verify your bag. Please retry before checking out.');
    }).then(function () {
      checkoutRunning = false;
      if (button && button.classList) { button.classList.remove('diy-loading'); button.classList.remove('btn--loading'); }
    });
  }
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    var btn = e.target && e.target.closest && e.target.closest('button[name="checkout"], .cart__checkout, a[href]');
    if (!btn) return;
    if (btn.tagName === 'A') {
      try { var destination = new URL(btn.href, window.location.href); if (destination.origin !== window.location.origin || !/\/checkout\/?$/.test(destination.pathname)) return; }
      catch (error) { return; }
    }
    e.preventDefault(); e.stopImmediatePropagation();
    if (sizeSaveActive()) { showCartNotice(null, 'Your ring size is saving. Please wait for it to finish before checking out.'); return; }
    checkoutAttempt(btn.form || (btn.closest && btn.closest('form')), btn);
  }, true);
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !(form.id === 'CartPageForm' || form.querySelector('button[name="checkout"], .cart__checkout'))) return;
    // An explicit Update cart submit keeps its native meaning. Implicit Enter submits
    // and checkout buttons still validate; main-cart owns queuing submits during size saves.
    if (e.submitter && e.submitter.name !== 'checkout' && !(e.submitter.matches && e.submitter.matches('.cart__checkout'))) return;
    if (sizeSaveActive()) return;
    if (checkoutPermit === form && !sizeSaveActive() && !sizeGateActive()) { checkoutPermit = null; return; }
    if (e.defaultPrevented) return;
    e.preventDefault(); e.stopImmediatePropagation();
    if (!sizeSaveActive()) checkoutAttempt(form, e.submitter || form.querySelector('button[name="checkout"], .cart__checkout'));
  }, true);
  // Accelerated wallets bypass the form. Keep them shielded until the cart snapshot is safe,
  // and immediately invalidate that snapshot whenever the cart or ring-size save changes.
  var walletStyle = document.createElement('style');
  walletStyle.textContent = '.diy-cart-blocked .additional-checkout-buttons,.diy-cart-blocked shopify-accelerated-checkout-cart,.diy-size-saving .additional-checkout-buttons,.diy-size-saving shopify-accelerated-checkout-cart{pointer-events:none!important}';
  (document.head || document.documentElement).appendChild(walletStyle);
  setCheckoutBlocked(checkoutBlocked);
  ['pointerdown', 'touchend', 'click'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      var cached = arrivalSnapshot || readArrivalSnapshot(document);
      var dateWindowChanged = arrivalCartRelevant && cached && cached.timeSensitive !== false && cached.lines.some(function (line) { return line.eligible; }) && arrivalWindow(cached.generated * 1000) !== arrivalWindow(Date.now());
      if (!checkoutBlocked && !sizeSaveActive() && !sizeGateActive() && !dateWindowChanged) return;
      var path = e.composedPath ? e.composedPath() : [e.target];
      var wallet = path.some(function (el) { return el && el.matches && el.matches('.additional-checkout-buttons, .cart-wallets, shopify-accelerated-checkout-cart'); });
      if (wallet) { e.preventDefault(); e.stopImmediatePropagation(); showCartNotice(null, 'Please complete your ring and use Continue to Checkout. Your selection is saved.'); }
    }, true);
  });
  function refreshCartValidation() {
    var sequence = ++validationSequence, context = cartContext();
    setCheckoutBlocked(true);
    return getCart().then(function (cart) { if (sequence === validationSequence && context === cartContext()) applyCartValidation(cart); return cart; }).catch(function () { if (sequence === validationSequence) setCheckoutBlocked(true); });
  }
  document.addEventListener('diyona:cart-mutation', function (event) {
    var detail = event.detail || {}, id = detail.operationId || detail.source || 'legacy';
    ++arrivalReadGeneration;
    ++validationSequence;
    if (detail.phase === 'start' && detail.source !== 'arrival') ++externalMutationSequence;
    if (detail.phase === 'start') cartMutations[id] = true;
    else if (detail.phase === 'end') delete cartMutations[id];
    setCheckoutBlocked(true);
    if (!cartWriteActive() && !reconcileRunning && !ownedVerificationActive) refreshCartValidation();
  });
  document.addEventListener('diyona:cart-render', function (event) {
    var detail = event.detail || {};
    ++arrivalReadGeneration;
    ++validationSequence;
    renderActive = detail.phase === 'start';
    if (detail.phase === 'start' || detail.phase === 'error' || detail.phase === 'cancelled') renderNeedsRefresh = true;
    if (detail.phase === 'ready' && detail.cart) renderNeedsRefresh = false;
    setCheckoutBlocked(true);
    if (detail.phase === 'ready' && detail.cart) applyCartValidation(detail.cart);
    else if (detail.phase === 'error') showCartNotice(null, 'Your cart display could not be refreshed. Finish any edits, then retry before checking out.');
  });
  document.addEventListener('cart:updated', function (event) {
    if (event.detail && event.detail.rendered && event.detail.cart) { ++validationSequence; applyCartValidation(event.detail.cart); refreshArrivalAfterRender(event.detail.cart); }
    else refreshCartValidation();
  });
  document.addEventListener('diyona:size-state', refreshCartValidation);
  window.addEventListener('pageshow', function () { if (isCartPageLive()) initCartPage(); });
  function initCartPage() {
    if (reconcileRunning) return reconcileRunning;
    failedActive = false;
    var context = cartContext(), initialReadsLeft = 4;
    function readInitialCart(accept) {
      if (context !== cartContext()) return Promise.resolve();
      var sequence = ++validationSequence;
      initialReadsLeft--;
      return getCart().then(function (cart) {
        if (context !== cartContext()) return;
        if (sequence !== validationSequence) {
          // Keep a producer alive for a size save waiting on confirmation, but
          // never use an obsolete snapshot or spin forever under repeated edits.
          if (!initialReadsLeft) throw cartError('DIY_CART_CHANGED', 'Your bag changed while being confirmed. Please retry.');
          return readInitialCart(accept);
        }
        // Consume the validated snapshot in this same callback; do not add an
        // unguarded promise hop between the generation check and initialization.
        return accept(cart);
      });
    }
    setCheckoutBlocked(true);
    reconcileRunning = readInitialCart(function (cart) {
      var cancelled = cart.items.filter(function (line) { return isCancelled(operationOf(line)); }).map(function (line) { return line.key; });
      return (cancelled.length ? removeCartLines(cancelled) : Promise.resolve(cart)).then(function (current) {
        var pend = pendingLines(current);
        if (!pend.length && !cancelled.length && !renderNeedsRefresh) { setGate(false); applyCartValidation(current); return; }
        renderNeedsRefresh = true;
        setGate(true);
        return Promise.all(pend.map(function (line) { return reconcileWithRetry(line); })).then(function () {
          var finalSequence = ++validationSequence;
          return getCart().then(function (fresh) {
            if (finalSequence !== validationSequence || context !== cartContext()) throw cartError('DIY_CART_CHANGED', 'Your bag changed while being confirmed. Please retry.');
            var state = validateCheckoutCart(fresh);
            if (state.pending.length) {
              failedActive = true; wantsCheckout = false; setGate(true); setCheckoutBlocked(true); showCartNotice(fresh); return;
            }
            return silentFinalize().then(function (rendered) {
              if (!rendered || !Array.isArray(rendered.items)) throw cartError('DIY_CART_REFRESH', 'We could not refresh your bag. Please retry.');
              renderNeedsRefresh = false;
              setGate(false);
              applyCartValidation(rendered);
            });
          });
        });
      });
    }).catch(function (e) {
      console.error('[optimistic] init failed', e);
      failedActive = true; wantsCheckout = false; setCheckoutBlocked(true);
      if (e && e.code === 'DIY_CART_REFRESHING') return;
      showCartNotice(null, e && e.code === 'DIY_CART_EDITING' ? 'Finish editing your bag, then retry to show the confirmed prices.' : 'We could not verify your bag. Your selection is saved. Please retry.');
    }).then(function () { reconcileRunning = null; });
    return reconcileRunning;
  }

  // ── Ring-builder dedupe helpers (used by complete-ring's add-vs-update decision) ──
  // A shopper can't hold two of the same diamond. Collect EVERY cart line tied to `sku`
  // so the caller can fully remove the old ring/loose before re-adding (no orphans, and
  // robust to the reconcile add-then-remove window where two diamond lines coexist briefly).
  // Returns null, or { type:'ring'|'loose', diamondKey, settingKey, feeKey, settingVariantId, keys:[...] }.
  function findDiamondInCart(cart, sku) {
    var items = cart && cart.items || [];
    var diamonds = items.filter(function (li) { return matchesDiamondSku(li, sku); });
    var settings = items.filter(function (li) { return isSettingLine(li) && li.properties['Diamond SKU'] === sku; });
    if (!diamonds.length && !settings.length) return null;
    var exactFees = items.filter(function (li) { return isFeeLine(li) && li.properties._diamond_sku === sku; });
    var fees = exactFees.slice(), ambiguous = settings.length > 1 || diamonds.filter(function (li) { return !li.properties || !li.properties[PENDING_PROP]; }).length > 1 || exactFees.length > 1;
    var operations = {};
    diamonds.concat(settings, exactFees).forEach(function (line) { var id = (line.properties || {})[OP_PROP]; if (id) operations[id] = true; });
    if (Object.keys(operations).length > 1) ambiguous = true;
    if (!fees.length && settings.length === 1 && settings[0].properties['Custom Engraving']) {
      var text = settings[0].properties['Custom Engraving'];
      var possibleOwners = items.filter(function (li) { return isSettingLine(li) && li.properties['Custom Engraving'] === text; });
      var legacy = items.filter(function (li) { return isFeeLine(li) && !li.properties._diamond_sku && li.properties['Engraving Text'] === text; });
      if (legacy.length === 1 && possibleOwners.length === 1) fees = legacy;
      else if (legacy.length) ambiguous = true;
    }
    return { type: settings.length || diamonds.some(function (li) { var p = li.properties || {}; return p['Paired Setting'] || p._ring_type === 'Ring'; }) ? 'ring' : 'loose',
      diamondKey: diamonds[0] && diamonds[0].key || null, settingKey: settings[0] && settings[0].key || null,
      feeKey: fees[0] && fees[0].key || null, settingVariantId: settings[0] && settings[0].variant_id || null,
      keys: diamonds.concat(settings, fees).map(function (li) { return li.key; }), ambiguous: ambiguous };
  }

  // Remove cart lines by key (quantity 0 via /cart/update.js — keeps other lines untouched).
  function removeCartLines(keys) {
    if (!keys || !keys.length) return getCart();
    return getCart().then(function (before) {
      before.items.forEach(function (li) { if (keys.indexOf(li.key) !== -1) cancelPurchase(operationOf(li)); });
      var updates = {}; keys.forEach(function (key) { updates[key] = 0; });
      return postJson(cartRoute('cart/update.js'), { updates: updates }).catch(function () {}).then(getCart).then(function (fresh) {
        if (keys.some(function (key) { return lineInCart(fresh, key); })) throw cartError('DIY_CART_UNCERTAIN', 'Removal could not be confirmed. Please review your bag.');
        return fresh;
      });
    });
  }

  /* ---- Ring-size gate helpers (shared by cart page + header drawer) ---- */
  function isSettingLineItem(li) {
    var p = li.properties || {};
    return p._ring_builder === 'true' && !!p['Paired Diamond'];
  }
  // Lines that must carry a Ring Size before checkout: builder setting lines, plus any
  // line stamped `_needs_ring_size` (wedding bands added sizeless from the PDP/quick-view).
  function ringSizeMissingLines(cart) {
    return (cart.items || []).filter(function (li) {
      var p = li.properties || {};
      var sizable = isSettingLineItem(li) || p._needs_ring_size === 'true';
      return sizable && !(p['Ring Size'] && String(p['Ring Size']).replace(/\s+/g, ''));
    });
  }
  function cartNeedsRingSize(cart) { return ringSizeMissingLines(cart).length > 0; }
  // Poll until no line is mid-reconcile (_pending_diamond_id) or the deadline passes.
  // Mutating a pending sentinel's properties re-keys the line under reconcile()'s feet
  // (reconcile resolves the sentinel by its captured line.key), which can duplicate the
  // real diamond or orphan the sentinel — so size stamping always waits for a settled cart.
  function waitForNoPending(maxMs) {
    var deadline = Date.now() + (maxMs || 20000);
    function poll() {
      return getCart().then(function (cart) {
        var pending = pendingLines(cart).length > 0;
        if (!pending) return cart;
        if (failedActive || Date.now() >= deadline) {
          var error = new Error('Your diamond is still being confirmed. Please retry before saving a ring size.');
          error.code = 'DIY_DIAMOND_PENDING';
          throw error;
        }
        return new Promise(function (res) { setTimeout(res, 700); }).then(poll);
      });
    }
    return poll();
  }
  // Self-detect /cart page.
  function onReady(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }
  onReady(function () {
    if (isCartPageLive()) initCartPage();
    else refreshCartValidation();
  });

  window.DiyonaOptimisticCart = {
    SENTINEL_VARIANT: SENTINEL_VARIANT,
    optimisticAddRing: optimisticAddRing,
    replaceRing: replaceRing,
    getCart: getCart,
    addItemsVerified: addItemsVerified,
    cancelPurchase: cancelPurchase,
    optimisticAddLoose: optimisticAddLoose,
    buildRingLineItems: buildRingLineItems,
    buildLooseLineItem: buildLooseLineItem,
    showAtcAnimation: showAtcAnimation,
    hideAtcAnimation: hideAtcAnimation,
    findDiamondInCart: findDiamondInCart,
    removeCartLines: removeCartLines,
    ringSizeMissingLines: ringSizeMissingLines,
    cartNeedsRingSize: cartNeedsRingSize,
    waitForNoPending: waitForNoPending,
    validateCheckoutCart: validateCheckoutCart,
    retryPending: initCartPage,
    showCartNotice: showCartNotice,
    _initCartPage: initCartPage
  };
})();

